import DehazeIcon from '@mui/icons-material/Dehaze';
import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { isSameDay } from 'date-fns';
import { ChangeEvent, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AgCheckboxGroupPanel } from '../../common/AgCheckboxGroupPanel';
import { If } from '../../common/IfComponent';
import { PopperButton } from '../../common/PopperButton';
import { RootState } from '../../store';
import { canSeeStockColumn } from '../../utils';
import { openChooseExternalPricelistsDialog } from '../externalpricelist/redux/externalPricelistSlices';
import PricesMenuButton from '../price/PricesMenuButton';
import { getCurrentUserPermissions } from '../user/redux/userSelectors';
import { externalPriceInitialColDefs } from './externalPriceTypes';
import {
    getCheckboxFilters,
    getExternalPriceTable,
    getExternalPriceTableVisibleColumnKeys,
    getIsExternalPriceTableLoading,
    getSelectedExternalPricelistDate,
    getSelectedPricelistPeriods
} from './redux/externalPriceSelectors';
import {
    externalPriceTableActions,
    externalPricelistDateSelected,
    setIsExporting,
    showAllArticlesChanged,
    showCommentsOnlyChanged,
    showNewArticlesOnlyChanged
} from './redux/externalPriceSlices';

const mapStateToProps = (state: RootState) => ({
    selectedExternalPricelistDate: getSelectedExternalPricelistDate(state),
    selectedExternalPricelistPeriods: Object.values(getSelectedPricelistPeriods(state)),
    checkboxFilters: getCheckboxFilters(state),
    isLoading: getIsExternalPriceTableLoading(state),
    columnKeys: getExternalPriceTableVisibleColumnKeys(state),
    externalPriceTable: getExternalPriceTable(state),
    currentUserPermissions: getCurrentUserPermissions(state)
});
type Props = ReturnType<typeof mapStateToProps>;

const ExternalPriceButtons = ({
    selectedExternalPricelistDate,
    selectedExternalPricelistPeriods,
    checkboxFilters,
    isLoading,
    columnKeys,
    externalPriceTable,
    currentUserPermissions
}: Props) => {
    const dispatch = useDispatch();

    const handleCustDialogOpen = useCallback(
        () => dispatch(openChooseExternalPricelistsDialog()),
        [dispatch]
    );

    const handleSetExternalPricelistDate = (newDate: Date | null) => {
        if (isDateEnabled(newDate)) {
            dispatch(externalPricelistDateSelected(newDate.toISOString()));
        }
    };

    const handleShowAllArticles = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showAllArticlesChanged(event.target.checked));
    };

    const handleShowCommentsOnly = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showCommentsOnlyChanged(event.target.checked));
    };

    const handleShowNewArticlesOnly = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showAllArticlesChanged(event.target.checked));
        dispatch(showNewArticlesOnlyChanged(event.target.checked));
    };

    const isDateEnabled = (date: Date | null): date is Date => {
        if (date == null) {
            return false;
        }

        return selectedExternalPricelistPeriods.some(plp => isSameDay(new Date(plp.validFrom), date));
    };

    const handleColumnsSelected = (newColumns: string[]) => {
        dispatch(externalPriceTableActions.columnsSelected(newColumns));
    };

    return (
        <Grid container alignItems="flex-start" direction="row" wrap="nowrap" justifyContent="space-between">
            <Grid item>
                <Grid container spacing={2}>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={handleCustDialogOpen}>
                            Choose external pricelists
                        </Button>
                    </Grid>
                    <Grid item>
                        <If
                            condition={selectedExternalPricelistPeriods.length > 0}
                            then={
                                <DatePicker
                                    inputFormat="yyyy-MM-dd"
                                    label="Pricelist date"
                                    shouldDisableDate={date => !isDateEnabled(date)}
                                    value={selectedExternalPricelistDate}
                                    onChange={handleSetExternalPricelistDate}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            variant="standard"
                                            sx={theme => ({ marginTop: theme.spacing(1) })}
                                        />
                                    )}
                                />
                            }
                            else={null}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            disabled={selectedExternalPricelistPeriods.length === 0}
                            sx={theme => ({ marginTop: theme.spacing(1) })}
                            control={
                                <Checkbox
                                    checked={checkboxFilters.showAllArticles}
                                    onChange={handleShowAllArticles}
                                />
                            }
                            label="Show all articles"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            disabled={selectedExternalPricelistPeriods.length === 0}
                            sx={theme => ({ marginTop: theme.spacing(1) })}
                            control={
                                <Checkbox
                                    checked={checkboxFilters.showCommentsOnly}
                                    onChange={handleShowCommentsOnly}
                                />
                            }
                            label="Show comments only"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            disabled={selectedExternalPricelistPeriods.length === 0}
                            sx={theme => ({ marginTop: theme.spacing(1) })}
                            control={
                                <Checkbox
                                    checked={checkboxFilters.showNewArticlesOnly}
                                    onChange={handleShowNewArticlesOnly}
                                />
                            }
                            label="Show new articles only"
                        />
                    </Grid>
                    <Grid item>
                        <PricesMenuButton
                            onDownloadClicked={() => dispatch(setIsExporting(true))}
                            showTooltipsChangedAction={externalPriceTableActions.showTooltipsChanged}
                            tooltipsVisible={externalPriceTable.showTooltips}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <CircularProgress sx={!isLoading ? { visibility: 'hidden' } : undefined} />
                    </Grid>

                    <Grid item xs={6}>
                        <PopperButton btnIcon={<DehazeIcon />} btnTitle="Choose columns">
                            {/* <CheckboxGroupPanel
                                available={externalPriceInitialColumns}
                                selected={columnKeys.map(c => c.key)}
                                // setSelected={handleColumnsSelected}
                            /> */}
                            <AgCheckboxGroupPanel
                                available={externalPriceInitialColDefs.filter(colDef =>
                                    canSeeStockColumn(colDef.field, currentUserPermissions)
                                )}
                                selected={columnKeys.map(c => c.key)}
                                setSelected={handleColumnsSelected}
                            />
                        </PopperButton>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default connect(mapStateToProps)(ExternalPriceButtons);
